export interface WeatherLimits {
    Waves: { avoid: null|number, warn: null|number },
    SwellHeight: { avoid: null|number, warn: null|number },
    HeadSwellHeight: { avoid: null|number, warn: null|number },
    HeadSwellPeriod: { avoid: { max: null|number, min: null|number }, warn: { max: null|number, min: null|number } },
    BeamSwellHeight: { avoid: null|number, warn: null|number },
    BeamSwellPeriod: { avoid: { max: null|number, min: null|number }, warn: { max: null|number, min: null|number } },
    FollowingSwellHeight: { avoid: null|number, warn: null|number },
    FollowingSwellPeriod: { avoid: { max: null|number, min: null|number }, warn: { max: null|number, min: null|number } },
    Wind10M: { avoid: null|number, warn: null|number },
    Wind50M: { avoid: null|number, warn: null|number },
    Visibility: { avoid: null|number, warn: null|number },
    Icing: { avoid: null|number, warn: null|number },
    Hurricanes: { avoid: null|number, warn: null|number },
    TropicalStorms: { avoid: null|number, warn: null|number },
    Depressions: { avoid: null|number, warn: null|number },
    WindGusts10M: { avoid: null|number, warn: null|number },
    WindGusts50M: { avoid: null|number, warn: null|number },
    RiskWindSpeed: { avoid: null|number, warn: null|number },
    RiskWaveHeight: { avoid: null|number, warn: null|number },
    HeadWind: { avoid: null|number, warn: null|number },
    BeamWind: { avoid: null|number, warn: null|number },
    FollowingWind: { avoid: null|number, warn: null|number },
    BeamWavesHeight: { avoid: null|number, warn: null|number },
    BeamWavesPeriod: { avoid: { max: null|number, min: null|number }, warn: { max: null|number, min: null|number } },
    HeadWavesHeight: { avoid: null|number, warn: null|number },
    HeadWavesPeriod: { avoid: { max: null|number, min: null|number }, warn: { max: null|number, min: null|number } },
    FollowingWavesHeight: { avoid: null|number, warn: null|number },
    FollowingWavesPeriod: { avoid: { max: null|number, min: null|number }, warn: { max: null|number, min: null|number } },
    IceConcentration: { avoid: null|number, warn: null|number },
    MaxRollAngle: { avoid: null|number, warn: null|number }
};

interface RoutingAPIWeatherLimit
{
    type: string;
    action: string;
    min?: number;
    max?: number;
}

type LimitWithPeriod = "BeamWavesHeight"|"HeadWavesHeight"|"FollowingWavesHeight";

export function getVisibilityValueTexts()
{
    return ["Good", "Moderate/Good", "Moderate", "Moderate/Poor", "Poor"];
}

export function getIcingValueTexts()
{
    return ["None", "Light", "Moderate", "Severe", "Very severe"];
}

export function getWeatherInfoTexts() {
    return ["Clear", "Partly Cloudy", "Cloudy", "Fog", "Drizzle", "Rain", "Freezing Rain", "Snow", "Rain Shower", "Snow Shower", "Hail" , "Thunder"];
}

// Convert weather limits from the plan format to the format expected by the routing API
export function convertWeatherLimitsToRoutingAPI(weatherLimits?: WeatherLimits): RoutingAPIWeatherLimit[]
{
    if (!weatherLimits) {
        // Hurricanes limit should always be set
        return [
            {
                type: "Hurricanes",
                min: 370400,
                action: "Avoid"
            }
        ];
    }

    const limits: RoutingAPIWeatherLimit[] = [];
    if (!weatherLimits.Hurricanes.avoid) {
        // Hurricanes limit should always be set
        limits.push({
            type: "Hurricanes",
            min: 370400,
            action: "Avoid"
        });
    }

    for (const type in weatherLimits) {
        if (type.endsWith("Period")) {
            // Period types are handled along with the associated wave heights; skip it
            continue;
        }

        const weatherLimit = weatherLimits[<keyof WeatherLimits>type];
        const needsPeriod = ["BeamWavesHeight", "HeadWavesHeight", "FollowingWavesHeight"].includes(type);
        if (weatherLimit.warn !== null) {
            limits.push(getWeatherLimit(type, <number>weatherLimit.warn, "Warn"));
            if (needsPeriod) {
                const periodType = getMatchingPeriodType(<LimitWithPeriod>type);
                limits.push(getWeatherLimit(periodType, <{min: number, max: number}>weatherLimits[<keyof WeatherLimits>periodType].warn, "Warn"));
            }
        }
        if (weatherLimit.avoid !== null) {
            limits.push(getWeatherLimit(type, <number>weatherLimit.avoid, "Avoid"));
            if (needsPeriod) {
                const periodType = getMatchingPeriodType(<LimitWithPeriod>type);
                limits.push(getWeatherLimit(periodType, <{min: number, max: number}>weatherLimits[<keyof WeatherLimits>periodType].avoid, "Avoid"));
            }
        }
    }

    return limits;
}

function getWeatherLimit(type: string, value: number|{min: number, max: number}, action: string): RoutingAPIWeatherLimit
{
    const limit: RoutingAPIWeatherLimit =  {
        type, action
    };

    if (type.endsWith("Period"))
    {
        // Both min and max should be set
        limit.min = (<{min: number, max: number}>value).min;
        limit.max = (<{min: number, max: number}>value).max;
    }
    else
    {
        if (["Visibility", "Hurricanes", "TropicalStorms", "Depressions"].includes(type))
        {
            // Should be a minimum value
            limit.min = <number>value;
        }
        else
        {
            // Should be a maximum value
            limit.max = <number>value;
        }
    }

    return limit;
}

function getMatchingPeriodType(type: LimitWithPeriod): string
{
    switch (type)
    {
        case "BeamWavesHeight":
            return "BeamWavesPeriod";
        case "HeadWavesHeight":
            return "HeadWavesPeriod";
        case "FollowingWavesHeight":
            return "FollowingWavesPeriod";
    }
}